/**
 * phaserNativeEmitterAdapter
 * ──────────────────────────
 * F2: traduce un ParticleEffectDefV2 (catálogo central) en un
 * Phaser.GameObjects.Particles.ParticleEmitter nativo (Phaser 3.60+).
 *
 * Ventaja vs motor v1: batching nativo (1 draw call por blend mode/atlas en
 * vez de N sprites independientes), pool ya optimizado, no requiere update
 * loop manual desde GameScene (el emitter se actualiza solo).
 *
 * Lo que el adapter cubre:
 *   - emission (rate/burstCount/maxAlive/life)
 *   - shape (point | rect | circle edgeOnly/full)
 *   - velocity (random_rect | directional | radial mapeo a speed/angle)
 *   - alphaOverLife (2-stop; multi-stop usa pico-aware en startValue)
 *   - sizeOverLife (2-stop; idem)
 *   - rotation (spin range)
 *   - tint (pick aleatorio por partícula via onEmit)
 *   - frames (pick aleatorio)
 *   - frameScales (multiplica scale base via onEmit)
 *   - blendMode
 *   - loop + duration (frequency/quantity para burst vs continuous)
 *
 * Lo que NO cubre (usa Processors separados — ver OrbitProcessor):
 *   - orbit
 *   - depthMode 'orbit' (per-particle depth dinámico)
 *   - sub-emitters, trail, noise, colorOverLife multi-stop, curvas > 2 stops
 *     reales (se aproximan con start/end del nativo)
 */

import Phaser from 'phaser';
import type {
  ParticleEffectDefV2,
  NumberCurve,
  SpawnShapeModule,
  VelocityModule,
} from '@shared/types/particles';

// ────────────────────────────────────────────────────────────────────────────
// Helpers de curva (heurística pico-aware igual que v1v2Convert)
// ────────────────────────────────────────────────────────────────────────────

function peakOf(curve: NumberCurve | undefined, fallback: number): number {
  if (!curve || curve.length === 0) return fallback;
  return curve.reduce((max, [, v]) => (v > max ? v : max), -Infinity);
}

function lastOf(curve: NumberCurve | undefined, fallback: number): number {
  if (!curve || curve.length === 0) return fallback;
  return curve[curve.length - 1]?.[1] ?? fallback;
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers de blend mode
// ────────────────────────────────────────────────────────────────────────────

function toPhaserBlendMode(mode: ParticleEffectDefV2['blendMode']): Phaser.BlendModes {
  switch (mode) {
    case 'ADD':
      return Phaser.BlendModes.ADD;
    case 'SCREEN':
      return Phaser.BlendModes.SCREEN;
    case 'MULTIPLY':
      return Phaser.BlendModes.MULTIPLY;
    default:
      return Phaser.BlendModes.NORMAL;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// emitZone desde shape
// ────────────────────────────────────────────────────────────────────────────

function buildEmitZone(
  shape: SpawnShapeModule | undefined
): Phaser.Types.GameObjects.Particles.ParticleEmitterEdgeZoneConfig
  | Phaser.Types.GameObjects.Particles.ParticleEmitterRandomZoneConfig
  | undefined {
  if (!shape || shape.type === 'point') return undefined;

  if (shape.type === 'rect') {
    return {
      type: 'random',
      source: new Phaser.Geom.Rectangle(-shape.w / 2, -shape.h / 2, shape.w, shape.h),
    };
  }
  if (shape.type === 'circle') {
    if (shape.edgeOnly) {
      return {
        type: 'edge',
        source: { getPoints: (q) => new Phaser.Geom.Circle(0, 0, shape.radius).getPoints(q) },
        quantity: 64,
      };
    }
    return {
      type: 'random',
      source: new Phaser.Geom.Circle(0, 0, shape.radius),
    };
  }
  if (shape.type === 'ring') {
    // Random entre dos circles concéntricos — Phaser nativo no tiene ring zone;
    // se aproxima con edge del radio externo.
    return {
      type: 'edge',
      source: { getPoints: (q) => new Phaser.Geom.Circle(0, 0, shape.rOut).getPoints(q) },
      quantity: 64,
    };
  }
  if (shape.type === 'line') {
    return {
      type: 'edge',
      source: { getPoints: (q) => new Phaser.Geom.Line(shape.x1, shape.y1, shape.x2, shape.y2).getPoints(q) },
      quantity: 32,
    };
  }
  if (shape.type === 'arc') {
    // Aproximación con edge de círculo + slice angular en el onEmit (no soportado simple).
    return {
      type: 'edge',
      source: { getPoints: (q) => new Phaser.Geom.Circle(0, 0, shape.radius).getPoints(q) },
      quantity: 32,
    };
  }
  return undefined;
}

// ────────────────────────────────────────────────────────────────────────────
// Velocidad nativa
// ────────────────────────────────────────────────────────────────────────────

function applyVelocityConfig(
  velocity: VelocityModule | undefined,
  config: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig
): void {
  if (!velocity) {
    config.speedX = { min: -10, max: 10 };
    config.speedY = { min: -10, max: 10 };
    return;
  }
  switch (velocity.mode) {
    case 'random_rect':
      config.speedX = { min: velocity.vx[0], max: velocity.vx[1] };
      config.speedY = { min: velocity.vy[0], max: velocity.vy[1] };
      break;
    case 'directional':
      config.angle = { min: velocity.angle[0], max: velocity.angle[1] };
      config.speed = { min: velocity.speed[0], max: velocity.speed[1] };
      break;
    case 'radial':
      config.angle = { min: 0, max: 360 };
      config.speed = { min: velocity.speed[0], max: velocity.speed[1] };
      break;
    case 'tangential':
      // Nativo no soporta tangencial puro. Aproximación: speed angular alto
      // sin radial. Idealmente con un Processor custom (no en este MVP).
      config.angle = { min: 0, max: 360 };
      config.speed = { min: velocity.speed[0], max: velocity.speed[1] };
      break;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Adapter principal
// ────────────────────────────────────────────────────────────────────────────

export interface AdapterResult {
  /** Emitter nativo creado. Se posiciona/destroy desde el caller. */
  emitter: Phaser.GameObjects.Particles.ParticleEmitter;
  /** True si el adapter pudo mapear la def sin pérdidas críticas. */
  fullSupport: boolean;
  /** True si el emitter está configurado en modo burst (frequency = -1).
   *  El caller es responsable de llamar emitter.explode() para emitir. */
  isBurst: boolean;
}

/**
 * Crea un emitter nativo Phaser 3.60+ a partir de una def v2.
 * Devuelve null si la def usa features incompatibles (delegar a v1).
 *
 * @param moveTo Cuando está presente, configura `moveToX/Y` en local space al
 *   emitter — cada partícula viaja a ese punto al final de su lifespan
 *   (usado por proyectiles para hacer que el blob llegue al tile target).
 */
export function phaserNativeEmitterAdapter(
  scene: Phaser.Scene,
  def: ParticleEffectDefV2,
  x: number,
  y: number,
  moveTo?: { x: number; y: number }
): AdapterResult | null {
  if (!scene.textures.exists(def.spriteKey)) {
    console.warn(`[phaserNativeAdapter] texture "${def.spriteKey}" no cargada`);
    return null;
  }

  // Rechazar features que requieren motor v1 o Processors complejos no
  // implementados en este MVP del adapter. El caller hace fallback a v1.
  if (def.orbit) {
    return null; // F2.B+ con OrbitProcessor + doble emitter para depth dinámico
  }
  if (def.depthMode === 'orbit') {
    return null; // requiere doble emitter (front/back) — pendiente
  }
  if (def.subEmitters || def.trail || def.noise) {
    return null; // F6
  }

  // alpha + scale: 2-stop con heurística pico-aware
  const alphaStart = peakOf(def.alphaOverLife?.stops, 1);
  const alphaEnd = lastOf(def.alphaOverLife?.stops, 0);
  const scaleStart = peakOf(def.sizeOverLife?.stops, 1);
  const scaleEnd = lastOf(def.sizeOverLife?.stops, 0.5);

  const baseScales = def.frameScales ?? {};
  const hasFrameScales = Object.keys(baseScales).length > 0;

  const lifeMin = def.emission.life[0];
  const lifeMax = def.emission.life[1];

  // Frecuencia: rate > 0 → continuous con frequency = 1000/rate.
  // rate == 0 → burst único con frequency = -1 + quantity = burstCount.
  const isBurst = def.emission.rate <= 0;
  const burstCount = def.emission.burstCount ?? def.emission.maxAlive;

  const config: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig = {
    frame: def.frames,
    lifespan: { min: lifeMin, max: lifeMax },
    quantity: isBurst ? burstCount : 1,
    frequency: isBurst ? -1 : 1000 / def.emission.rate,
    maxAliveParticles: def.emission.maxAlive,
    alpha: { start: alphaStart, end: alphaEnd },
    blendMode: toPhaserBlendMode(def.blendMode),
  };

  // Scale config — con frameScales usamos onEmit/onUpdate para que la curva
  // (scaleStart → scaleEnd) se multiplique por el factor del frame asignado
  // a cada partícula. Sin hooks de preUpdate (que acumulan exponencialmente).
  if (hasFrameScales) {
    config.scale = {
      onEmit: (particle: Phaser.GameObjects.Particles.Particle | null) => {
        if (!particle) return scaleStart;
        const frame = typeof particle.frame === 'object'
          ? Number((particle.frame as { name?: string | number }).name ?? 0)
          : Number(particle.frame ?? 0);
        const factor = baseScales[frame] ?? 1;
        (particle as unknown as { __frameScale?: number }).__frameScale = factor;
        return scaleStart * factor;
      },
      onUpdate: (
        particle: Phaser.GameObjects.Particles.Particle | null,
        _key: string,
        t: number
      ) => {
        const factor = particle
          ? (particle as unknown as { __frameScale?: number }).__frameScale ?? 1
          : 1;
        return (scaleStart + (scaleEnd - scaleStart) * t) * factor;
      },
    } as unknown as Phaser.Types.GameObjects.Particles.EmitterOpOnEmitType<number>;
  } else {
    config.scale = { start: scaleStart, end: scaleEnd };
  }

  // Rotación / spin
  if (def.rotation) {
    config.rotate = {
      onEmit: () =>
        Phaser.Math.FloatBetween(def.rotation!.spin[0], def.rotation!.spin[1]),
    };
  }

  // Tints como pick aleatorio por partícula
  const tints = def.tint && def.tint.length > 0 ? def.tint : null;
  if (tints) {
    config.tint = tints;
  }

  // Velocidad
  applyVelocityConfig(def.velocity, config);

  // Gravity
  if (def.physics) {
    if (def.physics.gravityX) config.gravityX = def.physics.gravityX;
    if (def.physics.gravityY) config.gravityY = def.physics.gravityY;
  }

  // Spawn zone (shape)
  const emitZone = buildEmitZone(def.shape);
  if (emitZone) {
    config.emitZone = emitZone;
  }

  // moveTo: cuando se setea, cada partícula recalcula su velocidad cada update
  // para llegar a (moveToX, moveToY) en local al final de su lifespan. Ver
  // Particle.js:617-625. Usado por proyectiles para que el blob converja al
  // tile target sin tener que mover el emitter ni hacer shift manual.
  if (moveTo) {
    config.moveToX = moveTo.x;
    config.moveToY = moveTo.y;
  }

  // Crear el emitter
  const emitter = scene.add.particles(x, y, def.spriteKey, config);
  emitter.setBlendMode(toPhaserBlendMode(def.blendMode));

  // (frameScales se maneja en config.scale arriba via onEmit/onUpdate.)

  // Loop / duration: si NO loop y duration > 0, parar emisión al cumplirse.
  if (!def.loop && def.duration > 0 && !isBurst) {
    scene.time.delayedCall(def.duration, () => {
      emitter.stop();
    });
  }

  // Para modo burst con frequency=-1, el emitter NO emite automáticamente al
  // crearse. El caller debe llamar `emitter.explode(quantity, x, y)`. Si el
  // caller no lo hace, el emitter queda inerte (no se rompe nada).

  return {
    emitter,
    fullSupport: !def.orbit && !def.subEmitters && !def.trail && !def.noise,
    isBurst,
  };
}
